import StringUtil from '@/util/StringUtil';
import {
    Autocomplete,
    autocompleteClasses,
    ListSubheader,
    Popper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material';
import { red } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import React, { Component } from 'react';
import { VariableSizeList } from 'react-window';

const LISTBOX_PADDING = 8; // px

class AutoComplete extends Component {
    constructor(props) {
        super(props);
    }

    handleOptionEqualToValue = (option, value) => {
        return option.texto === (typeof value === 'object' ? value.valor || value.texto : value);
    };

    handleFilterOptions = (options, value) => {
        let valor = value && StringUtil.normalizarString(value.inputValue);
        return this.opcoesContemValor(options, valor);
    };

    opcoesContemValor(options, valor) {
        if (!valor) {
            return options;
        }

        // Check if the value contains commas for multi-term search
        if (valor.includes(',') && this.props.buscaMultivalorada) {
            const termos = valor
                .split(',')
                .map((termo) => termo.trim())
                .filter((termo) => termo.length > 0);

            return options.filter((option) => {
                const textoNormalizado = StringUtil.normalizarString(option.texto);
                const textoSecundarioNormalizado = option.textoSecundario
                    ? StringUtil.normalizarString(option.textoSecundario)
                    : '';

                // Return true if ANY of the terms matches
                return termos.some(
                    (termo) =>
                        this.textoContemValor(textoNormalizado, termo) ||
                        (textoSecundarioNormalizado && this.textoContemValor(textoSecundarioNormalizado, termo))
                );
            });
        }

        // Single term search (original behavior)
        return options.filter(
            (option) =>
                this.textoContemValor(StringUtil.normalizarString(option.texto), valor) ||
                (option.textoSecundario &&
                    this.textoContemValor(StringUtil.normalizarString(option.textoSecundario), valor))
        );
    }

    textoContemValor(texto, valor) {
        return texto && valor && texto.toString().toLowerCase().indexOf(valor) > -1;
    }

    optionDesabilitado(option) {
        const disabledOptions = this.props.getOptionDisabled || [];
        let iguais = false;
        disabledOptions.forEach((disabledOption) => {
            if (JSON.stringify(disabledOption) === JSON.stringify(option)) {
                return (iguais = true);
            }
        });
        return iguais;
    }

    render() {
        return (
            <div style={{ ...this.props.style, width: '100%' }}>
                <Autocomplete
                    autoHighlight
                    disableListWrap
                    freeSolo={
                        this.props.permitirQualquerValor
                            ? this.props.permitirQualquerValor
                            : this.props.freeSolo
                              ? this.props.freeSolo
                              : false
                    }
                    multiple={this.props.selecaoMultipla}
                    options={this.props.options || []}
                    isOptionEqualToValue={this.handleOptionEqualToValue}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.texto)}
                    filterOptions={this.handleFilterOptions}
                    disableClearable={this.props.desabilitarLimpar}
                    value={this.props.value || null}
                    onChange={(_evento, valor) => this.props.onChange(valor)}
                    onInputChange={this.props.onInputChange}
                    open={this.props.open}
                    onOpen={this.props.onOpen}
                    onBlur={this.props.onBlur}
                    disabled={this.props.disabled}
                    loading={this.props.loading}
                    loadingText={'Carregando...'}
                    renderInput={this.renderInput}
                    renderOption={(props, option) => [props, option]}
                    renderGroup={(params) => params}
                    getOptionDisabled={(option) => this.optionDesabilitado(option)}
                    slots={{
                        popper: this.renderPopper
                    }}
                    slotProps={{
                        listbox: {
                            component: ListboxComponent
                        }
                    }}
                />
            </div>
        );
    }

    renderInput = (params) => {
        return (
            <Tooltip
                title={this.props.title || this.props.label || ''}
                placement="top-start"
            >
                <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    label={this.props.label || ''}
                    autoFocus={this.props.autoFocus}
                    sx={{
                        '& .MuiInputLabel-shrink': {
                            color: this.props.destacarLabel ? red[800] : undefined,
                            fontWeight: this.props.destacarLabel ? 'bold' : undefined
                        }
                    }}
                    onKeyDown={(event) => {
                        if (this.props.blurOnEnter && event.key === 'Enter') {
                            const element = document.getElementById('no-print');

                            if (element !== null) {
                                element.focus();
                            }
                        }

                        if (this.props.onEnterKeyDown && event.key === 'Enter') {
                            this.props.onEnterKeyDown(params.inputProps.value);
                        }
                    }}
                    slotProps={{
                        inputLabel: { style: { fontSize: '0.8rem', maxWidth: 'calc(100% - 44px)' } }
                    }}
                />
            </Tooltip>
        );
    };

    renderPopper = (props) => {
        return (
            <Popper
                {...props}
                placement={this.props.alinhamentoCima ? 'top-start' : 'bottom-start'}
                sx={{
                    [`& .${autocompleteClasses.listbox}`]: {
                        boxSizing: 'border-box',
                        maxHeight: `${8 * 36 + 2 * LISTBOX_PADDING}px`, // Altura padrão de 8 itens (36px cada) + padding
                        overflowY: 'auto',
                        '& ul': {
                            padding: 0,
                            margin: 0,
                            maxHeight: 'none', // Remove o limite padrão do MUI (40vh)
                            overflow: 'visible' // Deixa o react-window controlar o scroll sozinho
                        }
                    },
                    '& .MuiAutocomplete-paper': {
                        width: '100%',
                        '& .MuiAutocomplete-listbox': {
                            minWidth: '100px'
                        }
                    },
                    minWidth: '100px',
                    maxWidth: '800px'
                }}
                style={{ zIndex: 1350 }}
            />
        );
    };
}

function renderRow(props) {
    const { data, index, style } = props;
    const dataSet = data[index];
    const inlineStyle = {
        ...style,
        top: style.top + LISTBOX_PADDING
    };
    const option = dataSet[1];

    // Caso seja um grupo (cabeçalho)
    if (Object.hasOwn(dataSet, 'group')) {
        return (
            <ListSubheader
                key={dataSet.key}
                component="div"
                style={inlineStyle}
            >
                {dataSet.group}
            </ListSubheader>
        );
    }

    // Caso seja uma opção normal
    const { key, ...optionProps } = dataSet[0];

    return (
        <Typography
            key={key}
            component="li"
            {...optionProps}
            noWrap
            style={inlineStyle}
        >
            <div
                style={{ display: 'flex', flexDirection: 'column' }}
                title={option.texto + (option.textoSecundario ? ' - ' + option.textoSecundario : '')}
            >
                <div>{option.texto}</div>
                {option.textoSecundario && (
                    <div style={{ fontSize: '14px', color: 'grey' }}>{option.textoSecundario}</div>
                )}
            </div>
        </Typography>
    );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef(function OuterElementType(props, ref) {
    const outerProps = React.useContext(OuterElementContext);
    return (
        <div
            ref={ref}
            {...props}
            {...outerProps}
        />
    );
});

function useResetCache(itemData) {
    const ref = React.useRef(null);

    const signature = React.useMemo(() => {
        return itemData
            .map((d) => {
                // Caso seja um cabeçalho de grupo (ListSubheader)
                if (Object.hasOwn(d, 'group')) {
                    return `group-${d.group}`;
                }

                // Caso seja uma opção (o formato do renderRow)
                if (d[1]) {
                    const option = d[1];
                    // Rastreia valor, texto e textoSecundario (que afetam a altura)
                    return `opt-${option.valor ?? ''}-${option.texto ?? ''}-${option.textoSecundario ?? ''}`;
                }

                return 'empty';
            })
            .join('|');
    }, [itemData]);

    React.useEffect(() => {
        if (ref.current) {
            // Invalida o cache a partir do índice 0
            ref.current.resetAfterIndex(0, true);
        }
    }, [signature]);

    return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
    // eslint-disable-next-line react/prop-types
    const { children, ...other } = props;
    const itemData = [];

    if (/** @type {JSX.Element} */ children) {
        // @ts-ignore
        // eslint-disable-next-line react/prop-types
        children.forEach((item) => {
            itemData.push(item);
            itemData.push(...(item.children || []));
        });
    }

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
        noSsr: true
    });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;
    const getChildSize = (child) => {
        if (Object.hasOwn(child, 'group')) {
            return 48;
        }
        return itemSize + (child[1]?.textoSecundario ? 16 : 0);
    };
    const getHeight = () => {
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };
    const getChildWidth = (child) => {
        if (child[1]) {
            return child[1].textoSecundario
                ? Math.max(child[1].texto.length, child[1].textoSecundario.length)
                : child[1].texto.length;
        }
        return 0;
    };
    const getWidth = () => {
        return Math.min(800, itemData.map(getChildWidth).reduce((a, b) => Math.max(a, b), 0) * 11) + 'px';
    };
    const gridRef = useResetCache(itemData);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemKey={(index, data) => {
                        const item = data[index];
                        const base = item?.[1]?.valor ?? item?.[1]?.texto;
                        const keyValue = StringUtil.fnv1a32(base != null ? `${base}-${index}` : index);
                        return keyValue;
                    }}
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width={getWidth()}
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

export default AutoComplete;
